<template>
  <Layout>
    <div class="tournament-wrapper">
      <StellarBackground backColor="#2d3434"/>
      <div class="tournament-container">
        <CyberLoading :is-loading="isLoading" />
        <div class="tournament-page" :class="{ 'content-loaded': !isLoading }">
          <router-view v-if="$route.params.id" />
          <template v-else>
            <div class="tournament-header">
              <h1 class="text-center">{{ t('tournament.title') }}</h1>
              <p class="text-center">{{ t('tournament.subtitle') }}</p>
            </div>

            <div class="tournament-section">
              <div class="tournament-grid prime-tournaments">
                <h2 class="tournament-section-title">
                  <v-icon class="skull-icon" color="#df824d">mdi-alpha-p-box</v-icon>
                  {{ t('tournament.categories.prime') }}
                </h2>
                <v-card
                  v-for="tournament in primeTournaments"
                  :key="tournament.id"
                  class="tournament-card"
                  elevation="0"
                  @click="() => handleTournamentClick(tournament.title)"
                  style="cursor: pointer;"
                >
                  <v-img
                    :src="tournament.image"
                    height="200"
                    cover
                    class="tournament-image"
                    @load="handleImageLoad(tournament.id)"
                  >
                    <template v-slot:placeholder>
                      <div v-if="!loadedImages.has(tournament.id)" class="d-flex align-center justify-center fill-height">
                        <v-progress-circular
                          indeterminate
                          color="primary"
                        ></v-progress-circular>
                      </div>
                    </template>
                  </v-img>

                  <v-card-title class="tournament-title">
                    {{ tournament.title }}
                  </v-card-title>

                  <v-card-subtitle class="tournament-date">
                    {{ tournament.date.toUpperCase() }}
                  </v-card-subtitle>

                  <v-card-text class="tournament-description">
                    {{ tournament.description }}
                  </v-card-text>

                  <v-card-actions class="tournament-actions">
                    <v-spacer></v-spacer>
                    <Button
                      :icon-end="'mdi-information'"
                      height="40"
                      @click.stop.prevent="handleTournamentInfoClick(tournament.title)"
                    >
                      {{ t('tournament.comingSoon') }}
                    </Button>
                  </v-card-actions>
                </v-card>
              </div>
            </div>

            <div class="tournament-section">
              <div class="tournament-grid core-tournaments">
                <h2 class="tournament-section-title">
                  <v-icon class="skull-icon" color="#df824d">mdi-alpha-c-box</v-icon>
                  {{ t('tournament.categories.core') }}
                </h2>
                <v-card
                  v-for="tournament in coreTournaments"
                  :key="tournament.id"
                  class="tournament-card"
                  elevation="0"
                  @click="() => handleTournamentClick(tournament.title)"
                  style="cursor: pointer;"
                >
                  <v-img
                    :src="tournament.image"
                    height="200"
                    cover
                    class="tournament-image"
                    @load="handleImageLoad(tournament.id)"
                  >
                    <template v-slot:placeholder>
                      <div v-if="!loadedImages.has(tournament.id)" class="d-flex align-center justify-center fill-height">
                        <v-progress-circular
                          indeterminate
                          color="primary"
                        ></v-progress-circular>
                      </div>
                    </template>
                  </v-img>

                  <v-card-title class="tournament-title">
                    {{ tournament.title }}
                  </v-card-title>

                  <v-card-subtitle class="tournament-date">
                    {{ tournament.date.toUpperCase() }}
                  </v-card-subtitle>

                  <v-card-text class="tournament-description">
                    {{ tournament.description }}
                  </v-card-text>

                  <v-card-actions class="tournament-actions">
                    <v-spacer></v-spacer>
                    <Button
                      :icon-end="'mdi-information'"
                      height="40"
                      @click.stop.prevent="handleTournamentInfoClick(tournament.title)"
                    >
                      {{ t('tournament.comingSoon') }}
                    </Button>
                  </v-card-actions>
                </v-card>
              </div>
            </div>

            <div class="tournament-section">
              <div class="tournament-grid solo-tournaments">
                <h2 class="tournament-section-title">
                  <v-icon class="skull-icon" color="#df824d">mdi-alpha-s-box</v-icon>
                  {{ t('tournament.categories.solo') }}
                </h2>
                <v-card
                  v-for="tournament in soloTournaments"
                  :key="tournament.id"
                  class="tournament-card"
                  elevation="0"
                  @click="() => handleTournamentClick(tournament.title)"
                  style="cursor: pointer;"
                >
                  <v-img
                    :src="tournament.image"
                    height="200"
                    cover
                    class="tournament-image"
                    @load="handleImageLoad(tournament.id)"
                  >
                    <template v-slot:placeholder>
                      <div v-if="!loadedImages.has(tournament.id)" class="d-flex align-center justify-center fill-height">
                        <v-progress-circular
                          indeterminate
                          color="primary"
                        ></v-progress-circular>
                      </div>
                    </template>
                  </v-img>

                  <v-card-title class="tournament-title">
                    {{ tournament.title }}
                  </v-card-title>

                  <v-card-subtitle class="tournament-date">
                    {{ tournament.date.toUpperCase() }}
                  </v-card-subtitle>

                  <v-card-text class="tournament-description">
                    {{ tournament.description }}
                  </v-card-text>

                  <v-card-actions class="tournament-actions">
                    <v-spacer></v-spacer>
                    <Button
                      :icon-end="'mdi-information'"
                      height="40"
                      @click.stop.prevent="handleTournamentInfoClick(tournament.title)"
                    >
                      {{ t('tournament.comingSoon') }}
                    </Button>
                  </v-card-actions>
                </v-card>
              </div>
            </div>

            <div class="tournament-section">
              <div class="tournament-grid fun-tournaments">
                <h2 class="tournament-section-title">
                  <v-icon class="skull-icon" color="#df824d">mdi-alpha-f-box</v-icon>
                  {{ t('tournament.categories.fun') }}
                </h2>
                <v-card
                  v-for="tournament in funTournaments"
                  :key="tournament.id"
                  class="tournament-card"
                  elevation="0"
                  @click="() => handleTournamentClick(tournament.title)"
                  style="cursor: pointer;"
                >
                  <v-img
                    :src="tournament.image"
                    height="200"
                    cover
                    class="tournament-image"
                    @load="handleImageLoad(tournament.id)"
                  >
                    <template v-slot:placeholder>
                      <div v-if="!loadedImages.has(tournament.id)" class="d-flex align-center justify-center fill-height">
                        <v-progress-circular
                          indeterminate
                          color="primary"
                        ></v-progress-circular>
                      </div>
                    </template>
                  </v-img>

                  <v-card-title class="tournament-title">
                    {{ tournament.title }}
                  </v-card-title>

                  <v-card-subtitle class="tournament-date">
                    {{ tournament.date.toUpperCase() }}
                  </v-card-subtitle>

                  <v-card-text class="tournament-description">
                    {{ tournament.description }}
                  </v-card-text>

                  <v-card-actions class="tournament-actions">
                    <v-spacer></v-spacer>
                    <Button
                      id="tournament-info-button"
                      :icon-end="'mdi-information'"
                      height="40"
                      @click.stop.prevent="handleTournamentInfoClick(tournament.title)"
                    >
                      {{ t('tournament.comingSoon') }}
                    </Button>
                  </v-card-actions>
                </v-card>
              </div>
            </div>
            <div class="bottom-links d-none d-md-flex">
              <a href="https://alterlan.be/privacy" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.privacy')"></a>
              <a href="https://alterlan.be/terms" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.terms')"></a>
              <router-link to="/contact" class="nav-link" v-html="t('menu.footerLinks.contact')"></router-link>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import Layout from '@/components/layout/Layout.vue'
import Button from '@/components/ui/Button.vue'
import CyberLoading from '@/components/ui/CyberLoading.vue'
import StellarBackground from '@/components/animations/StellarBackground.vue'
import chessBg from '@/assets/backgrounds/Chess - Fun.webp'
import chickenNuggetBg from '@/assets/backgrounds/Chicken Nuggets - Fun.webp'
import CS2Bg from '@/assets/backgrounds/Counter Strike 2 - Grand.webp'
import lolGrandBg from '@/assets/backgrounds/League of Legends - Grand.webp'
import lolNormalBg from '@/assets/backgrounds/League of Legends - Normal.webp'
import lolSoloBg from '@/assets/backgrounds/League of Legends - Solo.webp'
import marvelRivalBg from '@/assets/backgrounds/Marvel Rivals - Normal.webp'
import rocketLeagueBg from '@/assets/backgrounds/Rocket League - Normal.webp'
import rocketLeagueSoloBg from '@/assets/backgrounds/Rocket League - Solo.webp'
import tftBg from '@/assets/backgrounds/Teamfight Tactics - Solo.webp'
import trackManiaBg from '@/assets/backgrounds/Trackmania - Solo.webp'
import quizBg from '@/assets/backgrounds/Quiz - Fun.webp'
import CS2CoreBG from '@/assets/backgrounds/Counter Strike 2 - Core.webp'

const { t } = useI18n()
const router = useRouter()

interface Tournament {
  id: number
  title: string
  date: string
  startTime: string
  endTime: string
  description: string
  image: string
  registrationOpen: boolean
}

const isLoading = ref(true)
const loadedImages = ref(new Set())

const handleImageLoad = (tournamentId: number) => {
  loadedImages.value.add(tournamentId)
}

const formatUrlSlug = (title: string): string => {
  return title.toLowerCase().replace(/\s+/g, '-');
};

const handleTournamentClick = (tournamentTitle: string) => {
  router.push(`/tournaments/${formatUrlSlug(tournamentTitle)}`);
};

const handleTournamentInfoClick = (tournamentTitle: string) => {
  router.push(`/tournaments/${formatUrlSlug(tournamentTitle)}`);
};

//add event listener for the info button
const infoButton = document.getElementById('tournament-info-button')
if (infoButton) {
  infoButton.addEventListener('click', (event) => {
    event.stopPropagation()
    handleTournamentInfoClick(event.title)
  })
}


onMounted(() => {
  // Simulate loading time for dramatic effect
  setTimeout(() => {
    isLoading.value = false
  }, 1300)
})

const primeTournaments = computed<Tournament[]>(() => [
  {
    id: 1,
    title: t('tournament.games.prime.csgo.title'),
    date: t('tournament.games.prime.csgo.date'),
    startTime: t('tournament.games.prime.csgo.startTime'),
    endTime: t('tournament.games.prime.csgo.endTime'),
    description: t('tournament.games.prime.csgo.description'),
    image: CS2Bg,
    registrationOpen: true
  },
  {
    id: 2,
    title: t('tournament.games.prime.lol.title'),
    date: t('tournament.games.prime.lol.date'),
    startTime: t('tournament.games.prime.lol.startTime'),
    endTime: t('tournament.games.prime.lol.endTime'),
    description: t('tournament.games.prime.lol.description'),
    image: lolGrandBg,
    registrationOpen: false
  }
])

const coreTournaments = computed<Tournament[]>(() => [
  {
    id: 3,
    title: t('tournament.games.core.rocketLeague.title'),
    date: t('tournament.games.core.rocketLeague.date'),
    startTime: t('tournament.games.core.rocketLeague.startTime'),
    endTime: t('tournament.games.core.rocketLeague.endTime'),
    description: t('tournament.games.core.rocketLeague.description'),
    image: rocketLeagueBg,
    registrationOpen: true
  },
  {
    id: 4,
    title: t('tournament.games.core.marvelRival.title'),
    date: t('tournament.games.core.marvelRival.date'),
    startTime: t('tournament.games.core.marvelRival.startTime'),
    endTime: t('tournament.games.core.marvelRival.endTime'),
    description: t('tournament.games.core.marvelRival.description'),
    image: marvelRivalBg,
    registrationOpen: true
  },
  {
    id: 5,
    title: t('tournament.games.core.lol.title'),
    date: t('tournament.games.core.lol.date'),
    startTime: t('tournament.games.core.lol.startTime'),
    endTime: t('tournament.games.core.lol.endTime'),
    description: t('tournament.games.core.lol.description'),
    image: lolNormalBg,
    registrationOpen: false
  },
  {
    id: 6,
    title: t('tournament.games.core.csgo.title'),
    date: t('tournament.games.core.csgo.date'),
    startTime: t('tournament.games.core.csgo.startTime'),
    endTime: t('tournament.games.core.csgo.endTime'),
    description: t('tournament.games.core.csgo.description'),
    image: CS2CoreBG,
    registrationOpen: true
  }
])

const soloTournaments = computed<Tournament[]>(() => [
  {
    id: 7,
    title: t('tournament.games.solo.lol.title'),
    date: t('tournament.games.solo.lol.date'),
    startTime: t('tournament.games.solo.lol.startTime'),
    endTime: t('tournament.games.solo.lol.endTime'),
    description: t('tournament.games.solo.lol.description'),
    image: lolSoloBg,
    registrationOpen: true
  },
  {
    id: 8,
    title: t('tournament.games.solo.rocketLeague.title'),
    date: t('tournament.games.solo.rocketLeague.date'),
    startTime: t('tournament.games.solo.rocketLeague.startTime'),
    endTime: t('tournament.games.solo.rocketLeague.endTime'),
    description: t('tournament.games.solo.rocketLeague.description'),
    image: rocketLeagueSoloBg,
    registrationOpen: true
  },
  {
    id: 9,
    title: t('tournament.games.solo.tft.title'),
    date: t('tournament.games.solo.tft.date'),
    startTime: t('tournament.games.solo.tft.startTime'),
    endTime: t('tournament.games.solo.tft.endTime'),
    description: t('tournament.games.solo.tft.description'),
    image: tftBg,
    registrationOpen: false
  },
  {
    id: 10,
    title: t('tournament.games.solo.trackMania.title'),
    date: t('tournament.games.solo.trackMania.date'),
    startTime: t('tournament.games.solo.trackMania.startTime'),
    endTime: t('tournament.games.solo.trackMania.endTime'),
    description: t('tournament.games.solo.trackMania.description'),
    image: trackManiaBg,
    registrationOpen: true
  }
])

const funTournaments = computed<Tournament[]>(() => [
  {
    id: 11,
    title: t('tournament.games.fun.chickenNugget.title'),
    date: t('tournament.games.fun.chickenNugget.date'),
    startTime: t('tournament.games.fun.chickenNugget.startTime'),
    endTime: t('tournament.games.fun.chickenNugget.endTime'),
    description: t('tournament.games.fun.chickenNugget.description'),
    image: chickenNuggetBg,
    registrationOpen: true
  },
  {
    id: 12,
    title: t('tournament.games.fun.chess.title'),
    date: t('tournament.games.fun.chess.date'),
    startTime: t('tournament.games.fun.chess.startTime'),
    endTime: t('tournament.games.fun.chess.endTime'),
    description: t('tournament.games.fun.chess.description'),
    image: chessBg,
    registrationOpen: true
  },
  {
    id: 13,
    title: t('tournament.games.fun.quiz.title'),
    date: t('tournament.games.fun.quiz.date'),
    startTime: t('tournament.games.fun.quiz.startTime'),
    endTime: t('tournament.games.fun.quiz.endTime'),
    description: t('tournament.games.fun.quiz.description'),
    image: quizBg,
    registrationOpen: true
  }
])
</script>

<style scoped>
.tournament-wrapper {
  position: relative;
  min-height: calc(100vh - 80px);
  width: 100%;
}

:deep(.stellar-background) {
  position: fixed;
  top: 80px;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.tournament-container {
  position: relative;
  z-index: 1;
}

.tournament-page {
  position: relative;
  padding: 2rem;
  min-height: calc(100vh - 80px);
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;
}

.content-loaded {
  opacity: 1;
  transform: translateY(0);
}

.tournament-header {
  text-align: center;
  position: relative;
  margin-bottom: 3rem;
  margin-top: 3rem;
}

.tournament-header h1 {
  font-family: 'Airborne GP', sans-serif;
  font-size: 3.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #76c4ae;
  letter-spacing: 4px;
  position: relative;
  display: inline-block;
}

.tournament-header p {
  font-size: 1.2rem;
  width: 90%;
  color: #ccc;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

@media (max-width: 768px) {
  .tournament-header p {
    width: 90%;
  }
}

.tournament-section {
  margin-bottom: 4rem;
  position: relative;
}

.tournament-grid {
  display: grid;
  gap: 2rem;
  row-gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

.tournament-section-title {
  font-size: 1.6rem;
  font-weight: 500;
  margin-bottom: -0.5rem;
  color: #fff;
  letter-spacing: 2px;
  text-align: start;
  position: relative;
  display: flex;
  align-items: center;
  grid-column: 1 / -1;
}

.skull-icon {
  margin-right: 1rem;
  font-size: 2rem !important;
}

.prime-tournaments {
  grid-template-columns: repeat(2, 1fr);
}

.core-tournaments {
  grid-template-columns: repeat(2, 1fr);
}

.fun-tournaments {
  grid-template-columns: repeat(3, 1fr);
}

.solo-tournaments {
  grid-template-columns: repeat(2, 1fr);
}

@media (max-width: 1400px) {
  .prime-tournaments {
    grid-template-columns: repeat(2, 1fr);
  }
  .core-tournaments {
    grid-template-columns: repeat(2, 1fr);
  }
  .fun-tournaments {
    grid-template-columns: repeat(2, 1fr);
  }
  .solo-tournaments {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767px) {
  .tournament-page {
    padding: 1rem;
  }

  .tournament-header {
    padding: 1rem;
    margin-bottom: 2rem;
  }

  .tournament-header h1 {
    font-size: 1.9rem;
    letter-spacing: 2px;
  }

  .tournament-header p {
    width: 80%;
    font-size: 1rem;
    padding: 0 1rem;
  }

  .tournament-section-title {
    font-size: 1.3rem;
  }

  .prime-tournaments,
  .core-tournaments,
  .fun-tournaments,
  .solo-tournaments {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 425px) {
  .tournament-header {
    padding: 1rem;
    margin-bottom: 2rem;
  }

  .tournament-header h1 {
    font-size: 1.5rem;
    letter-spacing: 2px;
  }

  .tournament-header p {
    width: 100%;
    font-size: 1rem;
    padding: 0 1rem;
  }

  .tournament-section-title {
    font-size: 1.1rem;
  }
}

.tournament-card {
  background: #414a4a;
  overflow: hidden;
  box-shadow: #76c4ae 0 0, #76c4ae80 0 0;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.tournament-card:hover {
    box-shadow: rgba(118, 196, 174, 0.3) -10px 10px, rgba(118, 196, 174, 0.2) -20px 20px !important;
}

.tournament-image {
  transition: all 0.3s ease;
}

.tournament-title {
  color: #76c4ae !important;
  font-size: 1.5rem !important;
  font-weight: bold !important;
  padding: 1rem !important;
}

.tournament-date {
  color: #ffffff !important;
  font-size: 1rem !important;
  padding: 0 1rem !important;
  font-weight: bold !important;
  opacity: 1;
}

.tournament-description {
  color: #fff !important;
  min-height: 100px;
  padding: 1rem !important;
}

.tournament-actions {
  float: left;
  padding-bottom: 1.5rem;
}

.bottom-links {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 4rem;
  padding: 4rem 0 2rem 0;
  margin-top: 2rem;
}

.nav-link {
  color: white;
  min-width: 100px;
  text-align: center;
  text-decoration: none;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #e69b71;
}

@media (max-width: 768px) {
  .bottom-links {
    gap: 2rem;
  }

  .nav-link {
    font-size: 0.7rem;
  }
}

.disabled-button {
  pointer-events: none !important;
}

.disabled-button::before {
  pointer-events: auto !important;
}

.disabled-button:hover {
  cursor: not-allowed !important;
}
</style>
